import { BuilderSection } from "@/components/studio/builder-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { ReadOnlyBanner } from "@/components/studio/read-only-banner";
import { RefinementSection } from "@/components/studio/refinement-section";
import {
  WorkbookMobilePreview,
  type WorkbookPreviewBlock,
} from "@/components/studio/workbook-mobile-preview";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { useToast } from "@/hooks/use-toast";
import { categoryService } from "@/lib/api/categories.service";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { ContentWorkbookService } from "@/lib/api/workbook.service";
import { api } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import type {
  Category,
  Challenge,
  TagGroup,
  TagOption,
  WorkbookBlock,
  WorkbookBlockType,
  WorkbookBlockVariant,
  WorkbookContent,
  WorkbookContentVariant,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  Globe,
  Headphones,
  Loader2,
  PenLine,
  Plus,
  Target,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

const BLOCK_TYPE_CONFIG: Partial<
  Record<WorkbookBlockType, { label: string; icon: typeof FileText }>
> = {
  prompt: { label: "Prompt", icon: FileText },
  journaling: { label: "Journaling", icon: PenLine },
  challenge: { label: "Audio Challenge", icon: Headphones },
};

const SUPPORTED_BLOCK_TYPES: WorkbookBlockType[] = [
  "prompt",
  "journaling",
  "challenge",
];

interface WorkbookEditData extends WorkbookContent {
  blocks: (WorkbookBlock & { variants: WorkbookBlockVariant[] })[];
  variants?: WorkbookContentVariant[];
}

interface VariantDraft {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedPrompt: string;
  localizedInstructions: string;
  localizedSectionTitle: string;
}

interface BlockDraft {
  id?: string;
  type: WorkbookBlockType;
  order: number;
  challengeId: string | null;
  variants: VariantDraft[];
}

interface WorkbookVariantDraft {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedSubtitle: string;
}

export default function WorkbookEditPage({ id }: { id: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [blocks, setBlocks] = useState<BlockDraft[]>([]);
  const [internalNotes, setInternalNotes] = useState("");
  const [selectedBlockIndex, setSelectedBlockIndex] = useState(0);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [pendingRemoveBlockIndex, setPendingRemoveBlockIndex] = useState<
    number | null
  >(null);
  const [pendingRemoveVariantKey, setPendingRemoveVariantKey] = useState<{
    blockIdx: number;
    variantIdx: number;
  } | null>(null);
  const [workbookVariants, setWorkbookVariants] = useState<
    WorkbookVariantDraft[]
  >([]);
  const [activeWorkbookVariantIndex, setActiveWorkbookVariantIndex] =
    useState(0);
  const [
    pendingRemoveWorkbookVariantIndex,
    setPendingRemoveWorkbookVariantIndex,
  ] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [inlineChallengeBlockIndex, setInlineChallengeBlockIndex] = useState<
    number | null
  >(null);
  const [inlineChallengeTitle, setInlineChallengeTitle] = useState("");
  const [inlineChallengeAudioFileUrl, setInlineChallengeAudioFileUrl] =
    useState("");
  const [inlineChallengeAudioFileName, setInlineChallengeAudioFileName] =
    useState("");
  const [inlineChallengeDurationSeconds, setInlineChallengeDurationSeconds] =
    useState("");
  const [inlineChallengeThumbnailUrl, setInlineChallengeThumbnailUrl] =
    useState("");
  const [categoryId, setCategoryId] = useState("");
  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const { data: workbookData, isLoading } = useQuery<WorkbookEditData>({
    queryKey: ["/content-workbooks", id],
    queryFn: async () => {
      const wb = await ContentWorkbookService.findOne(id);
      const versions = (wb as any).versions || [];
      const latestVersion = versions[0] || {};
      return {
        ...wb,
        variants: latestVersion.languages || [],
        version: latestVersion.version,
        status: latestVersion.status,
        internalNotes: latestVersion.internalNotes,
      } as WorkbookEditData;
    },
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: challengesResponse } = useQuery<{
    data: Challenge[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ["/content-challenges"],
  });
  const challenges = challengesResponse?.data ?? [];

  const inlineChallengeMutation = useMutation({
    mutationFn: async () => {
      const bLang = workbookData?.baseLanguage || "en";
      const challenge = await api.post<{ id: string }>("/content-challenges", {
        version: {
          languages: [
            {
              languageCode: bLang,
              isDefault: true,
              localizedTitle: inlineChallengeTitle,
              audioFileUrl: inlineChallengeAudioFileUrl || undefined,
              audioFileName: inlineChallengeAudioFileName || undefined,
              durationSeconds: inlineChallengeDurationSeconds
                ? parseInt(inlineChallengeDurationSeconds, 10)
                : undefined,
              thumbnailUrl: inlineChallengeThumbnailUrl || undefined,
            },
          ],
        },
      });
      return challenge;
    },
    onSuccess: (challenge) => {
      queryClient.invalidateQueries({ queryKey: ["/content-challenges"] });
      if (inlineChallengeBlockIndex !== null) {
        updateBlockChallengeId(inlineChallengeBlockIndex, challenge.id);
      }
      setInlineChallengeBlockIndex(null);
      setInlineChallengeTitle("");
      setInlineChallengeAudioFileUrl("");
      setInlineChallengeAudioFileName("");
      setInlineChallengeDurationSeconds("");
      setInlineChallengeThumbnailUrl("");
      toast({ title: "Challenge created and linked" });
    },
    onError: () => {
      toast({ title: "Failed to create challenge", variant: "destructive" });
    },
  });

  useEffect(() => {
    if (!workbookData) return;
    const nextKey = `${id}:${(workbookData as unknown as { version?: number }).version ?? 0}`;
    if (loadedKey === nextKey) return;
    {
      setInternalNotes(workbookData.internalNotes || "");
      setCategoryId(workbookData.categoryId || "");
      setSelectedTagOptionIds(workbookData.tagOptionIds || []);
      setBlocks(
        (workbookData.blocks || [])
          .filter((block) =>
            SUPPORTED_BLOCK_TYPES.includes(block.type as WorkbookBlockType),
          )
          .sort((a, b) => a.order - b.order)
          .map((block) => ({
            id: block.id,
            type: block.type,
            order: block.order,
            challengeId: block.challengeId,
            variants: (block.variants || []).map((v) => ({
              id: v.id,
              languageCode: v.languageCode,
              isDefault: v.isDefault,
              localizedTitle: v.localizedTitle,
              localizedPrompt: v.localizedPrompt || "",
              localizedInstructions: v.localizedInstructions || "",
              localizedSectionTitle: v.localizedSectionTitle || "",
            })),
          })),
      );
      const loadedVariants: WorkbookVariantDraft[] = (
        workbookData.variants || []
      ).map((v) => ({
        id: v.id,
        languageCode: v.languageCode,
        isDefault: v.isDefault,
        localizedTitle: v.localizedTitle,
        localizedSubtitle: v.localizedSubtitle || "",
      }));
      if (loadedVariants.length === 0) {
        loadedVariants.push({
          languageCode: workbookData.baseLanguage || "en",
          isDefault: true,
          localizedTitle: "",
          localizedSubtitle: "",
        });
      }
      setWorkbookVariants(loadedVariants);
      setLoadedKey(nextKey);
    }
  }, [workbookData, loadedKey, id]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/content-workbooks/${id}`, {
        categoryId: categoryId || undefined,
        tagOptionIds: selectedTagOptionIds,
        internalNotes: internalNotes || null,
        languages: workbookVariants
          .filter((wv) => wv.localizedTitle.trim())
          .map((wv) => ({
            ...(wv.id ? { id: wv.id } : {}),
            languageCode: wv.languageCode,
            isDefault: wv.isDefault,
            localizedTitle: wv.localizedTitle,
            localizedSubtitle: wv.localizedSubtitle || null,
          })),
        blocks: blocks.map((block, idx) => ({
          ...(block.id ? { id: block.id } : {}),
          type: block.type,
          order: idx,
          challengeId: block.challengeId || null,
          variants: block.variants.map((v) => ({
            ...(v.id ? { id: v.id } : {}),
            languageCode: v.languageCode,
            isDefault: v.isDefault,
            localizedTitle: v.localizedTitle,
            localizedPrompt: v.localizedPrompt || null,
            localizedInstructions: v.localizedInstructions || null,
            localizedSectionTitle: v.localizedSectionTitle || null,
          })),
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-workbooks"] });
      queryClient.invalidateQueries({
        queryKey: ["/content-workbooks", id],
      });
      toast({ title: "Workbook saved" });
    },
    onError: () => {
      toast({ title: "Failed to save workbook", variant: "destructive" });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      await saveMutation.mutateAsync();
      await api.post(`/content-workbooks/${id}/submit`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-workbooks"] });
      queryClient.invalidateQueries({
        queryKey: ["/content-workbooks", id],
      });
      toast({ title: "Submitted for approval" });
      setLocation("/studio/workbooks");
    },
    onError: (err: any) => {
      toast({
        title: err?.message || "Failed to submit",
        variant: "destructive",
      });
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: async () => {
      return await api.post<{ id: string; version: number }>(
        `/content-workbooks/${id}/create-version`,
        {},
      );
    },
    onSuccess: (data: { id: string; version: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/content-workbooks"] });
      toast({ title: `New version (v${data.version}) created as draft` });
      setLocation(`/studio/workbooks/${data.id}/edit`);
    },
    onError: () => {
      toast({ title: "Failed to create new version", variant: "destructive" });
    },
  });

  const addBlock = (type: WorkbookBlockType) => {
    const newBlock: BlockDraft = {
      type,
      order: blocks.length,
      challengeId: null,
      variants: [
        {
          languageCode: workbookData?.baseLanguage || "en",
          isDefault: true,
          localizedTitle: "",
          localizedPrompt: "",
          localizedInstructions: "",
          localizedSectionTitle: "",
        },
      ],
    };
    setBlocks([...blocks, newBlock]);
    setSelectedBlockIndex(blocks.length);
    setActiveVariantIndex(0);
  };

  const addWorkbookVariant = (langCode: string) => {
    if (workbookVariants.some((v) => v.languageCode === langCode)) return;
    const next = [
      ...workbookVariants,
      {
        languageCode: langCode,
        isDefault: false,
        localizedTitle: "",
        localizedSubtitle: "",
      },
    ];
    setWorkbookVariants(next);
    setActiveWorkbookVariantIndex(next.length - 1);
  };

  const updateWorkbookVariantField = (
    index: number,
    field: keyof WorkbookVariantDraft,
    value: string,
  ) => {
    const next = [...workbookVariants];
    next[index] = { ...next[index], [field]: value };
    setWorkbookVariants(next);
  };

  const confirmRemoveWorkbookVariant = () => {
    if (pendingRemoveWorkbookVariantIndex === null) return;
    const idx = pendingRemoveWorkbookVariantIndex;
    if (workbookVariants[idx]?.isDefault) {
      setPendingRemoveWorkbookVariantIndex(null);
      return;
    }
    const next = workbookVariants.filter((_, i) => i !== idx);
    setWorkbookVariants(next);
    if (activeWorkbookVariantIndex >= next.length) {
      setActiveWorkbookVariantIndex(Math.max(0, next.length - 1));
    }
    setPendingRemoveWorkbookVariantIndex(null);
  };

  const getAvailableLanguagesForWorkbook = () =>
    SUPPORTED_LANGUAGES.filter(
      (lang) => !workbookVariants.some((v) => v.languageCode === lang.code),
    );

  const moveBlock = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    const updated = [...blocks];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((b, i) => (b.order = i));
    setBlocks(updated);
    if (selectedBlockIndex === index) setSelectedBlockIndex(newIndex);
    else if (selectedBlockIndex === newIndex) setSelectedBlockIndex(index);
  };

  const confirmRemoveBlock = () => {
    if (pendingRemoveBlockIndex === null) return;
    const updated = blocks.filter((_, i) => i !== pendingRemoveBlockIndex);
    updated.forEach((b, i) => (b.order = i));
    setBlocks(updated);
    if (selectedBlockIndex >= updated.length) {
      setSelectedBlockIndex(Math.max(0, updated.length - 1));
    }
    setActiveVariantIndex(0);
    setPendingRemoveBlockIndex(null);
  };

  const updateBlockChallengeId = (blockIndex: number, challengeId: string) => {
    const updated = [...blocks];
    updated[blockIndex] = {
      ...updated[blockIndex],
      challengeId: challengeId === "none" ? null : challengeId,
    };
    setBlocks(updated);
  };

  const addVariantToBlock = (blockIndex: number, langCode: string) => {
    const updated = [...blocks];
    const block = { ...updated[blockIndex] };
    if (block.variants.some((v) => v.languageCode === langCode)) return;
    block.variants = [
      ...block.variants,
      {
        languageCode: langCode,
        isDefault: false,
        localizedTitle: "",
        localizedPrompt: "",
        localizedInstructions: "",
        localizedSectionTitle: "",
      },
    ];
    updated[blockIndex] = block;
    setBlocks(updated);
    setActiveVariantIndex(block.variants.length - 1);
  };

  const confirmRemoveVariant = () => {
    if (!pendingRemoveVariantKey) return;
    const { blockIdx, variantIdx } = pendingRemoveVariantKey;
    const updated = [...blocks];
    const block = { ...updated[blockIdx] };
    if (block.variants[variantIdx].isDefault) {
      setPendingRemoveVariantKey(null);
      return;
    }
    if (block.variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveVariantKey(null);
      return;
    }
    block.variants = block.variants.filter((_, i) => i !== variantIdx);
    updated[blockIdx] = block;
    setBlocks(updated);
    if (activeVariantIndex >= block.variants.length) {
      setActiveVariantIndex(Math.max(0, block.variants.length - 1));
    }
    setPendingRemoveVariantKey(null);
  };

  const updateVariantField = (
    blockIndex: number,
    variantIndex: number,
    field: keyof VariantDraft,
    value: string,
  ) => {
    const updated = [...blocks];
    const block = { ...updated[blockIndex] };
    const variants = [...block.variants];
    variants[variantIndex] = { ...variants[variantIndex], [field]: value };
    block.variants = variants;
    updated[blockIndex] = block;
    setBlocks(updated);
  };

  const getAvailableLanguagesForBlock = (blockIndex: number) => {
    const block = blocks[blockIndex];
    const workbookCodes = new Set(
      workbookVariants.map((v) => v.languageCode),
    );
    const scope = SUPPORTED_LANGUAGES.filter((lang) =>
      workbookCodes.has(lang.code),
    );
    if (!block) return scope;
    return scope.filter(
      (lang) => !block.variants.some((v) => v.languageCode === lang.code),
    );
  };

  const activeBlock = blocks[selectedBlockIndex];
  const activeVariant = activeBlock?.variants[activeVariantIndex];

  const { withdrawMutation, isReadOnly, isOwner, canCreateVersion } =
    useContentLifecycle({
    contentType: "workbook",
    apiPath: "/content-workbooks",
    id,
    status: workbookData?.status,
    createdBy: workbookData?.createdBy,
    listRoute: "/studio/workbooks",
    editRoute: (newId) => `/studio/workbooks/${newId}/edit`,
  });

  const primaryTitle =
    workbookVariants.find((v) => v.isDefault)?.localizedTitle ||
    workbookVariants[0]?.localizedTitle ||
    blocks[0]?.variants.find((v) => v.isDefault)?.localizedTitle ||
    "Workbook";
  const workbookConfig = CONTENT_TYPE_CONFIG.workbook;

  const getGroupOptions = (groupId: string) => {
    return tagOptions.filter((opt) => opt.groupId === groupId);
  };

  const previewPageVariant =
    workbookVariants[activeWorkbookVariantIndex] ||
    workbookVariants.find((v) => v.isDefault) ||
    workbookVariants[0];
  const previewLanguageCode = previewPageVariant?.languageCode || "en";
  const previewBlocks: WorkbookPreviewBlock[] = blocks.map((b) => {
    const v =
      b.variants.find((vv) => vv.languageCode === previewLanguageCode) ||
      b.variants.find((vv) => vv.isDefault) ||
      b.variants[0];
    let challenge: WorkbookPreviewBlock["challenge"] = null;
    if (b.type === "challenge" && b.challengeId) {
      const linked = challenges.find((c) => c.id === b.challengeId) as
        | (Challenge & {
            primaryTitle?: string;
            primaryAudioFileName?: string | null;
            primaryDurationSeconds?: number | null;
            primaryThumbnailUrl?: string | null;
          })
        | undefined;
      if (linked) {
        challenge = {
          title: linked.primaryTitle ?? null,
          audioFileName: linked.primaryAudioFileName ?? null,
          durationSeconds: linked.primaryDurationSeconds ?? null,
          thumbnailUrl: linked.primaryThumbnailUrl ?? null,
        };
      }
    }
    return {
      type: b.type,
      localizedSectionTitle: v?.localizedSectionTitle?.trim()
        ? v.localizedSectionTitle
        : null,
      title: v?.localizedTitle || "",
      prompt: v?.localizedPrompt?.trim() ? v.localizedPrompt : null,
      instructions: v?.localizedInstructions?.trim()
        ? v.localizedInstructions
        : null,
      challenge,
    };
  });
  const previewCategoryName =
    categories.find((c) => c.id === categoryId)?.title || null;

  const previewButton = (
    <Button
      variant="outline"
      onClick={() => setPreviewOpen(true)}
      data-testid="button-preview"
    >
      <Eye className="h-4 w-4 mr-2" />
      Preview
    </Button>
  );

  return (
    <>
      <BuilderShell
        contentTypeConfig={workbookConfig}
        mode="edit"
        title={primaryTitle}
        version={workbookData?.version}
        status={workbookData?.status}
        isLoading={isLoading}
        notFound={!isLoading && !workbookData}
        isReadOnly={isReadOnly}
        isSaving={saveMutation.isPending}
        isSubmitting={submitMutation.isPending}
        isCreatingVersion={createVersionMutation.isPending}
        canCreateVersion={canCreateVersion}
        isWithdrawing={withdrawMutation.isPending}
        onSave={() => saveMutation.mutate()}
        onSubmit={() => submitMutation.mutate()}
        onCreateVersion={() => createVersionMutation.mutate()}
        onWithdraw={() => withdrawMutation.mutate(undefined)}
        headerActions={previewButton}
      >
        {isReadOnly && <ReadOnlyBanner ownedByOther={!isOwner} />}

        <IntentTaggingSection
          stepNumber={1}
          categories={categories}
          categoryId={categoryId}
          onCategoryChange={(val) => {
            if (!isReadOnly) setCategoryId(val);
          }}
          tagGroups={tagGroups}
          getGroupOptions={getGroupOptions}
          selectedTagOptionIds={selectedTagOptionIds}
          onToggleTagOption={(id) => {
            if (isReadOnly) return;
            setSelectedTagOptionIds((prev) =>
              prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
            );
          }}
          isReadOnly={isReadOnly}
        />

        <BuilderSection
          title="Page Header"
          stepNumber={2}
          description="Title and subtitle shown at the top of the workbook page (per language)"
          defaultOpen={true}
          disabled={isReadOnly}
        >
          <div className="flex items-center gap-2 flex-wrap border-b pb-3 mb-4">
            {workbookVariants.map((v, i) => {
              const lang = SUPPORTED_LANGUAGES.find(
                (l) => l.code === v.languageCode,
              );
              return (
                <div key={v.languageCode} className="flex items-center gap-1">
                  <Button
                    variant={
                      i === activeWorkbookVariantIndex ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => setActiveWorkbookVariantIndex(i)}
                    data-testid={`tab-page-variant-${v.languageCode}`}
                  >
                    <Globe className="h-3.5 w-3.5 mr-1.5" />
                    {lang?.label || v.languageCode}
                    {v.isDefault && (
                      <Badge
                        variant="secondary"
                        className="ml-1.5 text-[10px] no-default-hover-elevate no-default-active-elevate"
                      >
                        Primary
                      </Badge>
                    )}
                  </Button>
                  {!v.isDefault && !isReadOnly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setPendingRemoveWorkbookVariantIndex(i)}
                      data-testid={`button-remove-page-variant-${v.languageCode}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
            {!isReadOnly && getAvailableLanguagesForWorkbook().length > 0 && (
              <Select onValueChange={(val) => addWorkbookVariant(val)}>
                <SelectTrigger
                  className="w-auto gap-1"
                  data-testid="button-add-page-language"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span className="text-sm">Add Language</span>
                </SelectTrigger>
                <SelectContent>
                  {getAvailableLanguagesForWorkbook().map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          {workbookVariants[activeWorkbookVariantIndex] && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Page Title *</Label>
                <Input
                  value={
                    workbookVariants[activeWorkbookVariantIndex].localizedTitle
                  }
                  onChange={(e) =>
                    updateWorkbookVariantField(
                      activeWorkbookVariantIndex,
                      "localizedTitle",
                      e.target.value,
                    )
                  }
                  placeholder="e.g. Put Yourself In Their Shoes"
                  disabled={isReadOnly}
                  data-testid="input-page-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Subtitle</Label>
                <Textarea
                  value={
                    workbookVariants[activeWorkbookVariantIndex]
                      .localizedSubtitle
                  }
                  onChange={(e) =>
                    updateWorkbookVariantField(
                      activeWorkbookVariantIndex,
                      "localizedSubtitle",
                      e.target.value,
                    )
                  }
                  placeholder="e.g. A workbook to help you reflect, choose your path, and walk it with intention."
                  rows={2}
                  disabled={isReadOnly}
                  data-testid="input-page-subtitle"
                />
              </div>
            </div>
          )}
        </BuilderSection>

        <BuilderSection
          title="Block Structure"
          stepNumber={3}
          description="Manage the blocks in this workbook"
          defaultOpen={true}
          disabled={isReadOnly}
        >
          {!isReadOnly && (
            <div className="flex flex-wrap gap-2 mb-4">
              {(
                Object.entries(BLOCK_TYPE_CONFIG) as [
                  WorkbookBlockType,
                  { label: string; icon: typeof FileText },
                ][]
              ).map(([type, config]) => {
                const Icon = config.icon;
                return (
                  <Button
                    key={type}
                    variant="outline"
                    size="sm"
                    onClick={() => addBlock(type)}
                    data-testid={`button-add-block-${type}`}
                  >
                    <Plus className="h-4 w-4 mr-1.5" />
                    <Icon className="h-4 w-4 mr-1.5" />
                    {config.label}
                  </Button>
                );
              })}
            </div>
          )}

          {blocks.length === 0 ? (
            <div className="text-center py-8" data-testid="text-no-blocks">
              <p className="text-sm text-muted-foreground">
                No blocks added yet. Use the buttons above to add blocks.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {blocks.map((block, index) => {
                const config = BLOCK_TYPE_CONFIG[block.type];
                if (!config) return null;
                const Icon = config.icon;
                return (
                  <div
                    key={block.id || `new-${index}`}
                    className={`flex items-center gap-3 p-3 rounded-md border transition-colors ${
                      selectedBlockIndex === index
                        ? "border-primary bg-primary/5"
                        : "border-border"
                    }`}
                    data-testid={`block-item-${index}`}
                  >
                    {!isReadOnly && (
                      <div className="flex flex-col gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveBlock(index, "up")}
                          disabled={index === 0}
                          data-testid={`button-move-up-${index}`}
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveBlock(index, "down")}
                          disabled={index === blocks.length - 1}
                          data-testid={`button-move-down-${index}`}
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                    <button
                      className="flex items-center gap-2 flex-1 min-w-0 text-left"
                      onClick={() => {
                        setSelectedBlockIndex(index);
                        setActiveVariantIndex(0);
                      }}
                      data-testid={`button-select-block-${index}`}
                    >
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium text-foreground">
                        {config.label}
                      </span>
                      <Badge
                        variant="secondary"
                        className="no-default-hover-elevate no-default-active-elevate"
                      >
                        #{index + 1}
                      </Badge>
                    </button>
                    {block.type === "challenge" && !isReadOnly && (
                      <div className="flex items-center gap-2">
                        <Select
                          value={block.challengeId || "none"}
                          onValueChange={(val) =>
                            updateBlockChallengeId(index, val)
                          }
                        >
                          <SelectTrigger
                            className="w-[180px]"
                            data-testid={`select-challenge-${index}`}
                          >
                            <SelectValue placeholder="Link challenge" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              No linked challenge
                            </SelectItem>
                            {challenges.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                Challenge {c.id.slice(0, 8)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setInlineChallengeBlockIndex(index)}
                          data-testid={`button-create-challenge-${index}`}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          New
                        </Button>
                      </div>
                    )}
                    {block.type === "challenge" &&
                      isReadOnly &&
                      block.challengeId && (
                        <Badge
                          variant="outline"
                          className="no-default-hover-elevate no-default-active-elevate"
                          data-testid={`badge-challenge-${index}`}
                        >
                          Challenge {block.challengeId.slice(0, 8)}
                        </Badge>
                      )}
                    {!isReadOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setPendingRemoveBlockIndex(index)}
                        data-testid={`button-remove-block-${index}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </BuilderSection>

        {activeBlock && (
          <BuilderSection
            title="Block Content"
            stepNumber={4}
            description={`Edit content for ${BLOCK_TYPE_CONFIG[activeBlock.type]?.label ?? "block"} #${selectedBlockIndex + 1}`}
            defaultOpen={true}
            disabled={isReadOnly}
          >
            <div className="flex items-center gap-2 flex-wrap border-b pb-3 mb-4">
              {activeBlock.variants.map((v, i) => {
                const lang = SUPPORTED_LANGUAGES.find(
                  (l) => l.code === v.languageCode,
                );
                return (
                  <div key={v.languageCode} className="flex items-center gap-1">
                    <Button
                      variant={i === activeVariantIndex ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveVariantIndex(i)}
                      data-testid={`tab-variant-${selectedBlockIndex}-${v.languageCode}`}
                    >
                      <Globe className="h-3.5 w-3.5 mr-1.5" />
                      {lang?.label || v.languageCode}
                      {v.isDefault && (
                        <Badge
                          variant="secondary"
                          className="ml-1.5 text-[10px] no-default-hover-elevate no-default-active-elevate"
                        >
                          Primary
                        </Badge>
                      )}
                    </Button>
                    {!v.isDefault && !isReadOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setPendingRemoveVariantKey({
                            blockIdx: selectedBlockIndex,
                            variantIdx: i,
                          })
                        }
                        data-testid={`button-remove-variant-${selectedBlockIndex}-${v.languageCode}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
              {!isReadOnly &&
                getAvailableLanguagesForBlock(selectedBlockIndex).length >
                  0 && (
                  <Select
                    onValueChange={(val) =>
                      addVariantToBlock(selectedBlockIndex, val)
                    }
                  >
                    <SelectTrigger
                      className="w-auto gap-1"
                      data-testid={`button-add-language-${selectedBlockIndex}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span className="text-sm">Add Language</span>
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableLanguagesForBlock(selectedBlockIndex).map(
                        (lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                )}
            </div>

            {activeVariant && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={activeVariant.localizedTitle}
                    onChange={(e) =>
                      updateVariantField(
                        selectedBlockIndex,
                        activeVariantIndex,
                        "localizedTitle",
                        e.target.value,
                      )
                    }
                    placeholder="Enter block title"
                    disabled={isReadOnly}
                    data-testid="input-block-title"
                  />
                </div>
                {activeBlock.type !== "journaling" && (
                  <div className="space-y-2">
                    <Label>Section Title (optional)</Label>
                    <Input
                      value={activeVariant.localizedSectionTitle}
                      onChange={(e) =>
                        updateVariantField(
                          selectedBlockIndex,
                          activeVariantIndex,
                          "localizedSectionTitle",
                          e.target.value,
                        )
                      }
                      placeholder="e.g. Week 1: Awareness — groups consecutive blocks under this heading"
                      disabled={isReadOnly}
                      data-testid="input-block-section-title"
                    />
                    <p className="text-xs text-muted-foreground">
                      Consecutive blocks sharing the same section title are
                      rendered as a single grouped section in the mobile app
                      (e.g. roadmap weeks).
                    </p>
                  </div>
                )}
                {activeBlock.type === "challenge" ? (
                  <div
                    className="rounded-md border border-border p-4 text-sm text-muted-foreground"
                    data-testid="text-challenge-audio-note"
                  >
                    <Target className="h-4 w-4 inline-block mr-2 align-middle" />
                    Challenges are audio-only experiences. Link or create a
                    challenge above to attach audio content to this block.
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Prompt</Label>
                      <Textarea
                        value={activeVariant.localizedPrompt}
                        onChange={(e) =>
                          updateVariantField(
                            selectedBlockIndex,
                            activeVariantIndex,
                            "localizedPrompt",
                            e.target.value,
                          )
                        }
                        placeholder="Enter the prompt text for this block"
                        rows={4}
                        disabled={isReadOnly}
                        data-testid="input-block-prompt"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Instructions</Label>
                      <Textarea
                        value={activeVariant.localizedInstructions}
                        onChange={(e) =>
                          updateVariantField(
                            selectedBlockIndex,
                            activeVariantIndex,
                            "localizedInstructions",
                            e.target.value,
                          )
                        }
                        placeholder="Enter instructions for this block"
                        rows={3}
                        disabled={isReadOnly}
                        data-testid="input-block-instructions"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </BuilderSection>
        )}

        <RefinementSection
          stepNumber={activeBlock ? 5 : 4}
          internalNotes={internalNotes}
          onInternalNotesChange={setInternalNotes}
          isReadOnly={isReadOnly}
        />

        <AlertDialog
          open={pendingRemoveWorkbookVariantIndex !== null}
          onOpenChange={(open) => {
            if (!open) setPendingRemoveWorkbookVariantIndex(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove page language?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove the page title/subtitle for this language.
                Block content for this language is unaffected.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-remove-page-variant">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmRemoveWorkbookVariant}
                data-testid="button-confirm-remove-page-variant"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={pendingRemoveBlockIndex !== null}
          onOpenChange={(open) => {
            if (!open) setPendingRemoveBlockIndex(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle data-testid="dialog-title-remove-block">
                Remove block?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pendingRemoveBlockIndex !== null && (
                  <>
                    This will remove{" "}
                    {BLOCK_TYPE_CONFIG[blocks[pendingRemoveBlockIndex]?.type]
                      ?.label || "this"}{" "}
                    block #{pendingRemoveBlockIndex + 1} and all its language
                    variants. This cannot be undone until you save.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-remove-block">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmRemoveBlock}
                data-testid="button-confirm-remove-block"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={pendingRemoveVariantKey !== null}
          onOpenChange={(open) => {
            if (!open) setPendingRemoveVariantKey(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle data-testid="dialog-title-remove-variant">
                Remove language variant?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pendingRemoveVariantKey !== null && (
                  <>
                    This will remove the{" "}
                    {SUPPORTED_LANGUAGES.find(
                      (l) =>
                        l.code ===
                        blocks[pendingRemoveVariantKey.blockIdx]?.variants[
                          pendingRemoveVariantKey.variantIdx
                        ]?.languageCode,
                    )?.label || "selected"}{" "}
                    variant and all its content. This cannot be undone until you
                    save.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-remove-variant">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmRemoveVariant}
                data-testid="button-confirm-remove-variant"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={inlineChallengeBlockIndex !== null}
          onOpenChange={(open) => {
            if (!open) setInlineChallengeBlockIndex(null);
          }}
        >
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>Create New Challenge</AlertDialogTitle>
              <AlertDialogDescription>
                Create a new challenge and automatically link it to this block.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={inlineChallengeTitle}
                  onChange={(e) => setInlineChallengeTitle(e.target.value)}
                  placeholder="Challenge title"
                  data-testid="input-inline-challenge-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Audio File URL</Label>
                <Input
                  value={inlineChallengeAudioFileUrl}
                  onChange={(e) =>
                    setInlineChallengeAudioFileUrl(e.target.value)
                  }
                  placeholder="https://example.com/audio.mp3"
                  data-testid="input-inline-challenge-audio-url"
                />
              </div>
              <div className="space-y-2">
                <Label>Audio File Name</Label>
                <Input
                  value={inlineChallengeAudioFileName}
                  onChange={(e) =>
                    setInlineChallengeAudioFileName(e.target.value)
                  }
                  placeholder="challenge-audio.mp3"
                  data-testid="input-inline-challenge-audio-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (seconds)</Label>
                <Input
                  type="number"
                  value={inlineChallengeDurationSeconds}
                  onChange={(e) =>
                    setInlineChallengeDurationSeconds(e.target.value)
                  }
                  placeholder="180"
                  data-testid="input-inline-challenge-duration"
                />
              </div>
              <div className="space-y-2">
                <Label>Thumbnail URL (optional)</Label>
                <Input
                  value={inlineChallengeThumbnailUrl}
                  onChange={(e) =>
                    setInlineChallengeThumbnailUrl(e.target.value)
                  }
                  placeholder="https://example.com/thumbnail.jpg"
                  data-testid="input-inline-challenge-thumbnail"
                />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-inline-challenge">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => inlineChallengeMutation.mutate()}
                disabled={
                  !inlineChallengeTitle.trim() ||
                  inlineChallengeMutation.isPending
                }
                data-testid="button-confirm-inline-challenge"
              >
                {inlineChallengeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : null}
                Create & Link
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </BuilderShell>

      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[480px] overflow-y-auto bg-neutral-100 dark:bg-neutral-900 p-0"
          data-testid="sheet-workbook-preview"
        >
          <SheetHeader className="px-6 pt-6 pb-4">
            <SheetTitle>Mobile preview</SheetTitle>
          </SheetHeader>
          <div className="px-4 pb-8 flex justify-center">
            <WorkbookMobilePreview
              pageTitle={previewPageVariant?.localizedTitle || ""}
              pageSubtitle={previewPageVariant?.localizedSubtitle || null}
              categoryName={previewCategoryName}
              blocks={previewBlocks}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
